import React, { useState, useMemo, useEffect } from "react";
import { Pagination, Table } from "flowbite-react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Search,
  Award,
  Loader2,
  GraduationCap,
  RefreshCw,
  Building2,
  Download,
  Users,
} from "lucide-react";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getToken } from "../utils/auth";

export default function HliGrantedStudents() {
  const [studyAreaId, setStudyAreaId] = useState("");
  const [academicYearId, setAcademicYearId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { data: institutionSettings = [] } = useQuery({
    queryKey: ["hliSettingsForGranted"],
    queryFn: async () => {
      const res = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/settings/getSettingsByMyInstitution",
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!getToken(),
  });

  const { data: academicYears = [] } = useQuery({
    queryKey: ["allAcademicYears"],
    queryFn: async () => {
      const res = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/academic/getAcademicYears"
      );
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const studyAreas = useMemo(() => {
    const seen = new Set();
    return institutionSettings
      .filter((s) => {
        if (!s.studentCategory || seen.has(s.studentCategory)) return false;
        seen.add(s.studentCategory);
        return true;
      })
      .map((s) => ({
        id: s.studentCategory,
        studyAreaName: s.studentCategory,
      }));
  }, [institutionSettings]);

  useEffect(() => {
    if (studyAreas.length > 0 && !studyAreaId) {
      setStudyAreaId(studyAreas[0].studyAreaName);
    }
  }, [studyAreas, studyAreaId]);

  const { data: loans = [], isPending, error, refetch } = useQuery({
    queryKey: ["hliGrantedLoans", studyAreaId, academicYearId],
    queryFn: async () => {
      const res = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/localLoan/getGrantedLocalLoansByMyInstitution",
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          params: { studyAreaId, academicYearId },
        }
      );
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: !!(studyAreaId && academicYearId),
    retry: 3,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    onError: (err) => {
      const msg = err.response?.data || "Failed to fetch granted students.";
      toast.error(typeof msg === "string" ? msg : "Failed to fetch granted students.", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    },
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [studyAreaId, academicYearId, searchTerm]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && studyAreaId && academicYearId) {
        refetch();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refetch, studyAreaId, academicYearId]);

  const filteredLoans = loans.filter((loan) => {
    const term = searchTerm.toLowerCase();
    return (
      loan.firstName?.toLowerCase().includes(term) ||
      loan.lastName?.toLowerCase().includes(term) ||
      loan.registrationNumber?.toLowerCase().includes(term) ||
      loan.collegeName?.toLowerCase().includes(term) ||
      loan.nationalId?.toLowerCase().includes(term) ||
      loan.email?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filteredLoans.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLoans = filteredLoans.slice(startIndex, endIndex);

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    if (filteredLoans.length === 0) {
      toast.error("No data to export", {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
      });
      return;
    }

    const excelData = filteredLoans.map((loan) => ({
      "Registration Number": loan.registrationNumber || "",
      "National ID": loan.nationalId || "",
      "First Name": loan.firstName ? loan.firstName.trim() : "",
      "Last Name": loan.lastName ? loan.lastName.trim() : "",
      "Gender": loan.gender || "",
      "Email": loan.email || "",
      "Phone": loan.phone || "",
      "Index Number": loan.indexNumber || "",
      "College Name": loan.collegeName || "",
      "Campus Name": loan.campusName || "",
      "Qualification Name": loan.qualificationName || "",
      "Qualification Code": loan.qualificationCode || "",
      "Education Level": loan.levelOfStudy || "",
      "Study Area": loan.studyArea || "",
      "Academic Year": loan.academicYearId || "",
      "Starting Year": loan.startingYear || "",
      "Ending Year": loan.endingYear || "",
      "Program Duration": loan.programDuration || "",
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet["!cols"] = [
      { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 10 },
      { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 20 },
      { wch: 40 }, { wch: 18 }, { wch: 15 }, { wch: 12 }, { wch: 15 },
      { wch: 12 }, { wch: 12 }, { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "Granted Students");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const currentDate = new Date().toISOString().split("T")[0];
    saveAs(blob, `Granted_Students_${studyAreaId.replace(" ", "_")}_${academicYearId}_${currentDate}.xlsx`);

    toast.success(`Excel exported successfully! (${filteredLoans.length} records)`, {
      position: "top-center",
      autoClose: 4000,
      theme: "colored",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>

      <div className="p-6 sm:ml-64 transition-all duration-300 flex-1 flex flex-col">
        {/* Page Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Granted Students</h1>
                  <p className="text-gray-600 mt-1">Students granted local loans from your institution</p>
                </div>
              </div>
              <div className="mt-6 lg:mt-0 flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 rounded-xl border border-green-200">
                  <Users className="w-5 h-5 text-green-600" />
                  <span className="font-semibold">{filteredLoans.length} Granted Students</span>
                </div>
                <button
                  onClick={() => refetch()}
                  disabled={!studyAreaId || !academicYearId}
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:text-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
                <button
                  onClick={exportToExcel}
                  disabled={filteredLoans.length === 0}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] ${
                    filteredLoans.length === 0
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  } text-white`}
                >
                  <Download className="w-5 h-5" />
                  <span>Export Excel</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Filters and Search Header */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Study Area</label>
                <select
                  value={studyAreaId}
                  onChange={(e) => { setStudyAreaId(e.target.value); setCurrentPage(1); }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm"
                >
                  <option value="">Select study area</option>
                  {studyAreas.map((sa) => (
                    <option key={sa.id} value={sa.studyAreaName}>{sa.studyAreaName}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Academic Year</label>
                <select
                  value={academicYearId}
                  onChange={(e) => { setAcademicYearId(e.target.value); setCurrentPage(1); }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm"
                >
                  <option value="">Select academic year</option>
                  {academicYears.map((ay) => (
                    <option key={ay.id} value={ay.academicYearName}>
                      {ay.academicYearName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2 lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Search Students</label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm placeholder-gray-500"
                    placeholder="Search by name, registration number, college..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="p-6">
            {!studyAreaId || !academicYearId ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <Building2 className="w-12 h-12 mb-3 text-cyan-200" />
                <p className="text-sm font-medium">Select study area and academic year to view granted students</p>
              </div>
            ) : isPending ? (
              <div className="flex items-center justify-center py-24">
                <div className="text-center">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-200 to-emerald-200 rounded-full animate-pulse mx-auto mb-4"></div>
                    <Loader2 className="w-8 h-8 text-green-600 animate-spin absolute top-4 left-1/2 transform -translate-x-1/2" />
                  </div>
                  <p className="text-gray-700 text-xl font-semibold mb-2">Loading Granted Students</p>
                  <p className="text-gray-500">Please wait while we fetch the latest data...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-24">
                <div className="w-20 h-20 bg-gradient-to-r from-red-200 to-pink-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="w-10 h-10 text-red-400" />
                </div>
                <p className="text-red-600 text-xl font-semibold mb-2">Error Loading Data</p>
                <p className="text-gray-600">Failed to load granted students. Please try again.</p>
                <button onClick={() => refetch()} className="mt-4 text-sm text-cyan-600 hover:underline">Try again</button>
              </div>
            ) : (
              <div>
                {currentLoans.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-gray-700">Show:</span>
                        <select
                          value={itemsPerPage}
                          onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                          className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value={5}>5 per page</option>
                          <option value={10}>10 per page</option>
                          <option value={25}>25 per page</option>
                          <option value={50}>50 per page</option>
                        </select>
                      </div>
                      <div className="text-sm text-gray-600">
                        Showing {startIndex + 1}–{Math.min(endIndex, filteredLoans.length)} of {filteredLoans.length} students
                      </div>
                    </div>

                    <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm">
                      <Table striped className="w-full">
                        <Table.Head className="bg-gradient-to-r from-gray-50 to-green-50">
                          <Table.HeadCell className="text-gray-700 font-semibold">Registration #</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">National ID</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Contact Info</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Study Period</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Institution</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Qualification</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Study Details</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Products</Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y divide-gray-200">
                          {currentLoans.map((loan, index) => (
                            <Table.Row
                              key={loan.id || index}
                              className="bg-white hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-200 border-b border-gray-100"
                            >
                              <Table.Cell className="whitespace-nowrap">
                                <div className="font-semibold text-gray-900 text-sm">
                                  {loan.registrationNumber || "—"}
                                </div>
                              </Table.Cell>

                              <Table.Cell className="whitespace-nowrap">
                                <div className="font-medium text-gray-900 text-sm">
                                  {loan.nationalId || "—"}
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-1">
                                  <div className="text-sm text-gray-900 font-medium">{loan.email || "—"}</div>
                                  <div className="text-xs text-gray-600">{loan.phone || "—"}</div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {loan.startingYear && loan.endingYear
                                      ? `${loan.startingYear} – ${loan.endingYear}`
                                      : "—"}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    Academic: {loan.academicYearId || "—"}
                                  </div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-1">
                                  <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                    {loan.collegeName || "—"}
                                  </div>
                                  <div className="text-xs text-gray-600">{loan.campusName || "—"}</div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-1">
                                  <div className="text-sm font-medium text-gray-900 max-w-xs">
                                    {loan.qualificationName || "—"}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    {loan.qualificationCode ? `Code: ${loan.qualificationCode}` : "—"}
                                  </div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-2">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    loan.studyArea === "STEM"
                                      ? "bg-blue-100 text-blue-800 border border-blue-200"
                                      : "bg-purple-100 text-purple-800 border border-purple-200"
                                  }`}>
                                    {loan.studyArea || "—"}
                                  </span>
                                  <div className="text-xs text-gray-600">
                                    {loan.levelOfStudy} • {loan.programDuration} years
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {loan.durationPerYear}
                                  </div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="max-w-xs">
                                  {loan.products && loan.products.length > 0 ? (
                                    <div className="text-xs text-gray-600 space-y-1">
                                      {loan.products.map((product, idx) => (
                                        <div key={idx} className="flex items-center space-x-2">
                                          <span className={`w-2 h-2 rounded-full ${product.isOnhold ? "bg-red-400" : "bg-green-400"}`}></span>
                                          <span className="text-xs font-medium">{product.productName}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">—</span>
                                  )}
                                </div>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>

                    {totalPages > 1 && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-8 pt-6 border-t border-gray-200 space-y-4 sm:space-y-0">
                        <div className="text-sm text-gray-600 font-medium">
                          Showing page {currentPage} of {totalPages} ({filteredLoans.length} total students)
                        </div>
                        <div className="flex items-center justify-center sm:justify-end">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            showIcons
                            theme={{
                              pages: {
                                base: "xs:mt-0 mt-2 inline-flex items-center -space-x-px",
                                showIcon: "inline-flex",
                                previous: {
                                  base: "ml-0 rounded-l-xl border border-gray-300 bg-white py-3 px-4 text-gray-500 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700 transition-all duration-200 shadow-sm",
                                },
                                next: {
                                  base: "rounded-r-xl border border-gray-300 bg-white py-3 px-4 text-gray-500 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700 transition-all duration-200 shadow-sm",
                                },
                                selector: {
                                  base: "w-12 border border-gray-300 bg-white py-3 text-gray-500 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700 transition-all duration-200 shadow-sm",
                                  active: "bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 border-green-500 shadow-md",
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-24">
                    <div className="w-20 h-20 bg-gradient-to-r from-gray-200 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      <GraduationCap className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-700 text-xl font-semibold mb-2">No Granted Students Found</p>
                    <p className="text-gray-500 max-w-md mx-auto">
                      {searchTerm
                        ? "No students match your search criteria. Try adjusting your search terms or filters."
                        : `No students have been granted loans for ${studyAreaId} in ${academicYearId}.`}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <FooterComponents />
      </div>
    </div>
  );
}
